import { axiosInstance } from '.'
import {
  TRADING_ACCOUNT_V1,
  TRADING_ASSET_SEARCH_V1,
  TRADING_CANCEL_TRANSFER_V1,
  TRADING_CHAT_V1,
  TRADING_ASSET_V1,
  TRADING_ASSET_BARS_V1,
  TRADING_ASSET_OVERVIEW_V1,
  TRADING_MARKET_STATUS_V1,
  TRADING_ORDER_PREVIEW_V1,
  TRADING_PORTFOLIO_HISTORY_V1,
  TRADING_PORTFOLIO_V1,
  TRADING_FUND_FROM_WALLET_V1,
  TRADING_ORDERS_V1,
  TRADING_ORDER_V1,
  TRADING_POSITIONS_V1,
  TRADING_QUOTES_V1,
  TRADING_WALLET_V1,
  TRADING_WITHDRAW_TO_WALLET_V1,
} from './endpoints'

export type TradingAccount = {
  status: string
  accountNumber: string | null
  buyingPower: string | null
  cash: string | null
}

export type TradingAsset = {
  symbol: string
  name: string
  exchange: string
  tradable?: boolean
  fractionable: boolean
}

export type OrderSide = 'buy' | 'sell'
export type OrderType = 'market' | 'limit' | 'stop' | 'stop_limit' | 'trailing_stop'
export type TimeInForce = 'day' | 'gtc' | 'ioc' | 'fok' | 'opg' | 'cls'

export type MarketStatus = {
  status: 'open' | 'pre_market' | 'after_hours' | 'closed'
  isOpen: boolean
  nextOpen: string | null
  nextClose: string | null
  asOf: string | null
}

export type StockQuote = {
  symbol: string
  price: number | null
  previousClose: number | null
  change: number | null
  changePercent: number | null
  bid: number | null
  ask: number | null
  open: number | null
  high: number | null
  low: number | null
  volume: number | null
  asOf: string | null
}

export type CompanyOverview = {
  symbol: string
  name: string
  exchange: string
  tradable: boolean
  fractionable: boolean
  shortable: boolean
  quote: StockQuote | null
  /** Minutes the prices lag behind the market (0 = real time). */
  delayMinutes: number
  market: MarketStatus | null
  position: {
    qty: number
    qtyAvailable: number
    avgEntryPrice: number
    marketValue: number
    unrealizedPl: number
  } | null
}

export type ChartRange = '1D' | '1W' | '1M' | '1Y'

export type PortfolioHolding = {
  symbol: string
  name: string
  exchange: string
  qty: number
  /** Shares not reserved by open sell orders. */
  qtyAvailable: number
  avgEntryPrice: number
  currentPrice: number
  marketValue: number
  costBasis: number
  dayPl: number
  dayPlPercent: number | null
  totalPl: number
  totalPlPercent: number | null
  /** Share of the whole portfolio, cash included. */
  allocationPercent: number | null
}

export type Portfolio = {
  equity: number
  cash: number
  buyingPower: number
  investedValue: number
  costBasis: number
  dayPl: number
  dayPlPercent: number | null
  totalPl: number
  totalPlPercent: number | null
  cashAllocationPercent: number | null
  holdings: PortfolioHolding[]
}

export type PortfolioRange = '1D' | '1W' | '1M' | '3M' | '1Y'

export type PortfolioHistory = {
  range: PortfolioRange
  points: Array<{ t: string; v: number }>
  /** Gain or loss over the range, excluding deposits and withdrawals. */
  change: number | null
  changePercent: number | null
}

export type PriceChart = {
  symbol: string
  range: ChartRange
  delayMinutes: number
  points: Array<{ t: string; c: number }>
}

export type OrderPreview = {
  order: {
    symbol: string
    side: OrderSide
    type: OrderType
    timeInForce: TimeInForce
    qty: number | null
    notional: number | null
    limitPrice: number | null
    stopPrice: number | null
    trailPrice: number | null
    trailPercent: number | null
    extendedHours: boolean
  }
  estimatedPrice: number
  estimatedQty: number
  estimatedTotal: number
  fee: number
  buyingPowerBefore: number | null
  buyingPowerAfter: number | null
  sharesHeld: number | null
  quoteAsOf: string | null
  delayMinutes: number
  market: MarketStatus | null
  warnings: Array<{ code: string; message: string }>
}

export type TradingTransfer = {
  id: string
  direction: 'INCOMING' | 'OUTGOING'
  /**
   * The other side of the transfer: the investment wallet (funding in, or
   * withdrawing out) or a bank account over ACH (transfers from before).
   */
  source?: 'GREENBACK_WALLET' | 'BANK'
  amount: string
  status: string
  createdAt: string
  /** Held for review by the brokerage; it may take longer to complete. */
  held?: boolean
  /** Can still be cancelled by the investor (only while held). */
  cancellable?: boolean
}

export type TradingWallet = TradingAccount & {
  equity: string | null
  portfolioValue: string | null
  investedValue: string | null
  /** Investment (Greenback) wallet balance that can be moved in, in USD. */
  greenbackWalletAvailable: string | null
  /** Brokerage cash that can be moved back to the investment wallet, in USD. */
  withdrawable?: string | null
  fundingMode?: 'prefunding' | 'instant_funding'
  /** Transfers above this many USD may be held for review; null when unknown. */
  transferReviewThresholdUsd?: number | null
  /** True when Alpaca's bank transfer history could not be loaded. */
  bankHistoryUnavailable?: boolean
  transfers: TradingTransfer[]
}

export type TradingOrder = {
  id: number
  alpaca_order_id: string
  symbol: string
  side: OrderSide
  type: OrderType
  time_in_force: string
  qty: string | null
  notional: string | null
  limit_price: string | null
  stop_price?: string | null
  trail_price?: string | null
  trail_percent?: string | null
  extended_hours?: boolean
  replaces_order_id?: string | null
  status: string
  filled_qty: string | null
  filled_avg_price: string | null
  created_at: string
}

export type TradingPosition = {
  symbol: string
  qty: string
  avg_entry_price: string
  market_value: string
  unrealized_pl: string
  unrealized_plpc: string
  /** Shares not reserved by open sell orders. */
  qty_available?: string
}

export type PlaceOrderPayload = {
  symbol: string
  side: OrderSide
  type: OrderType
  timeInForce: TimeInForce
  qty?: number
  notional?: number
  limitPrice?: number
  stopPrice?: number
  trailPrice?: number
  trailPercent?: number
  extendedHours?: boolean
}

/** Changes to an open order (cancel and replace). */
export type ReplaceOrderPayload = {
  qty?: number
  timeInForce?: TimeInForce
  limitPrice?: number
  stopPrice?: number
  trailPrice?: number
  trailPercent?: number
}

export type OpenAccountPayload = {
  givenName: string
  familyName: string
  dateOfBirth: string
  taxId: string
  taxIdType: string
  countryOfCitizenship: string
  countryOfBirth: string
  countryOfTaxResidence: string
  fundingSource: string[]
  phoneNumber: string
  streetAddress: string[]
  city: string
  state?: string
  postalCode: string
  country: string
  isControlPerson: boolean
  isAffiliatedExchangeOrFinra: boolean
  isPoliticallyExposed: boolean
  immediateFamilyExposed: boolean
  acceptedAgreements: boolean
}

export type ChatMessage = { role: 'user' | 'assistant'; content: string }

/** Results the chat assistant looked up, shown as cards under its reply. */
export type ChatCard =
  | { type: 'quote'; symbol: string; name: string | null; bid: number | null; ask: number | null }
  | {
      type: 'news'
      symbol: string
      items: { headline: string; source: string; date: string; url: string | null }[]
    }
  | {
      type: 'positions'
      positions: {
        symbol: string
        qty: string
        marketValue: string
        unrealizedPl: string
        unrealizedPlpc: string
      }[]
    }
  | { type: 'account'; cash: string | null; buyingPower: string | null }
  | {
      type: 'order'
      name: string | null
      order: PlaceOrderPayload
      estimatedPrice: number | null
      estimatedTotal: number | null
      buyingPower: string | null
    }

export type ChatReply = { reply: string; cards: ChatCard[] }

/** Every call resolves with the axios response (success or error) so callers check `status`. */
const request = async (run: () => Promise<any>) => {
  try {
    return await run()
  } catch (error: any) {
    return error.response
  }
}

export const tradingServices = {
  getWallet: () => request(() => axiosInstance.get(TRADING_WALLET_V1)),
  chat: (messages: ChatMessage[]) =>
    request(() => axiosInstance.post(TRADING_CHAT_V1, { messages })),
  getAccount: () => request(() => axiosInstance.get(TRADING_ACCOUNT_V1)),
  openAccount: (payload: OpenAccountPayload) =>
    request(() => axiosInstance.post(TRADING_ACCOUNT_V1, payload)),
  searchAssets: (q: string) =>
    request(() => axiosInstance.get(TRADING_ASSET_SEARCH_V1, { params: { q } })),
  getAsset: (symbol: string) => request(() => axiosInstance.get(TRADING_ASSET_V1(symbol))),
  getQuotes: (symbols: string[]) =>
    request(() => axiosInstance.get(TRADING_QUOTES_V1, { params: { symbols: symbols.join(',') } })),
  getCompany: (symbol: string) =>
    request(() => axiosInstance.get(TRADING_ASSET_OVERVIEW_V1(symbol))),
  getChart: (symbol: string, range: ChartRange) =>
    request(() => axiosInstance.get(TRADING_ASSET_BARS_V1(symbol), { params: { range } })),
  getPortfolio: () => request(() => axiosInstance.get(TRADING_PORTFOLIO_V1)),
  getPortfolioHistory: (range: PortfolioRange) =>
    request(() => axiosInstance.get(TRADING_PORTFOLIO_HISTORY_V1, { params: { range } })),
  getMarketStatus: () => request(() => axiosInstance.get(TRADING_MARKET_STATUS_V1)),
  previewOrder: (payload: PlaceOrderPayload) =>
    request(() => axiosInstance.post(TRADING_ORDER_PREVIEW_V1, payload)),
  placeOrder: (payload: PlaceOrderPayload) =>
    request(() => axiosInstance.post(TRADING_ORDERS_V1, payload)),
  replaceOrder: (orderId: string, payload: ReplaceOrderPayload) =>
    request(() => axiosInstance.patch(TRADING_ORDER_V1(orderId), payload)),
  getOrders: (status: 'open' | 'closed' | 'all' = 'all') =>
    request(() => axiosInstance.get(TRADING_ORDERS_V1, { params: { status } })),
  cancelOrder: (orderId: string) => request(() => axiosInstance.delete(TRADING_ORDER_V1(orderId))),
  getPositions: () => request(() => axiosInstance.get(TRADING_POSITIONS_V1)),
  fundFromWallet: (amount: number) =>
    request(() => axiosInstance.post(TRADING_FUND_FROM_WALLET_V1, { amount })),
  cancelTransfer: (id: string) => request(() => axiosInstance.post(TRADING_CANCEL_TRANSFER_V1(id))),
  withdrawToWallet: (amount: number) =>
    request(() => axiosInstance.post(TRADING_WITHDRAW_TO_WALLET_V1, { amount })),
}
